/* ==========================================================================
   Segal — "maximum reached" contact popup
   --------------------------------------------------------------------------
   WHY. When the generator is rate-limited the Worker used to answer with the
   generic "We don't have this name ready yet". Measured during QA on
   2026-09-01: after five names the API returned
   {status:"rate-limited", reason:"limit:ip"} and the customer was told the
   NAME was unavailable. That is not true, and on a $3,470 product it reads as
   "we can't make this" rather than "try again later" - a lost sale, silently.

   WHAT THIS DOES. It watches the response from /name-request. On a
   rate-limited answer it opens the theme's existing CUSTOMIZE popup - the same
   /contact#contact_form the CUSTOMIZE button opens, with the same fields the
   team already receives - swaps the intro line for the limit message, and
   pre-fills the name the customer was trying to make so the team knows what
   they wanted.

   IT REUSES THE THEME'S OWN POPUP AND FORM. No new markup, no new form, no new
   endpoint. It clicks the real `[data-js-popup-button="customize-product"]`
   trigger, so the theme's own open/close/scroll-lock behaviour applies exactly
   as it does for a normal CUSTOMIZE click. The intro line it swaps is restored
   the moment the popup closes, so pressing CUSTOMIZE normally is unaffected.

   FETCH WRAPPING. Same self-healing approach as name-necklace-family.js,
   because other scripts on this theme replace window.fetch late. To avoid the
   two wrappers endlessly re-wrapping each other, this one carries family's
   flag forward when it wraps family's function - so family.js sees its own
   flag still set and leaves the chain alone.
   ========================================================================== */
(() => {
  'use strict';

  const cfg = window.SegalNameNecklace || {};
  const target = String(cfg.requestUrl || '');
  if (!target) return;

  const M = window.SegalNameNecklaceMetrics = window.SegalNameNecklaceMetrics || {};
  M.limitPopupShown = 0;

  const POPUP_SEL   = '.popup-customize-product';
  const TRIGGER_SEL = '[data-js-popup-button="customize-product"]';
  const INTRO_SEL   = 'p.text-center';
  const MESSAGE_SEL = 'textarea[name="contact[message]"]';

  const LIMIT_TEXT = "You've reached the maximum custom name necklaces creation. " +
                     "Contact us and tell us what you'd like to see — we'll make it for you.";

  let originalIntro = null;   // restored when the popup closes
  let currentLimitText = LIMIT_TEXT;

  const restoreIntro = () => {
    if (originalIntro === null) return;
    const intro = document.querySelector(POPUP_SEL + ' ' + INTRO_SEL);
    if (intro) intro.textContent = originalIntro;
    originalIntro = null;
  };

  // The theme marks an open popup with `popup-opened` on <body>. When that
  // clears, the customer has closed it and the normal copy must come back.
  setInterval(() => {
    if (originalIntro !== null && !document.body.classList.contains('popup-opened')) restoreIntro();
  }, 400);

  const openLimitPopup = (name, message) => {
    currentLimitText = message || LIMIT_TEXT;
    const trigger = document.querySelector(TRIGGER_SEL);
    const popup = document.querySelector(POPUP_SEL);
    if (!trigger || !popup) return false;          // theme without the popup: leave the inline message alone

    const intro = popup.querySelector(INTRO_SEL);
    if (intro && originalIntro === null) originalIntro = intro.textContent;
    if (intro) intro.textContent = currentLimitText;

    const box = popup.querySelector(MESSAGE_SEL);
    if (box && !String(box.value || '').trim() && name) {
      box.value = 'The name I would like: ' + name + '\n\n';
      // Leave the caret after the prefill so they can keep typing.
      try { box.setSelectionRange(box.value.length, box.value.length); } catch (_) { }
    }

    // The switcher retries a failed request up to 3 times, so a single rate-limit can
    // arrive here 3 times. Re-clicking the trigger on an already-open popup makes it
    // flicker, so only open it when nothing is open.
    if (!document.body.classList.contains('popup-opened')) trigger.click();

    M.limitPopupShown += 1;
    M.lastLimitPopup = { name: name || null, at: new Date().toISOString() };
    return true;
  };

  // The switcher's failFlow clears busy state. Do not hold the UI in an error
  // for 45 seconds: customers must be able to select an existing design immediately.

  const wrap = () => {
    const current = window.fetch;
    if (!current || current.__segalLimitPatched) return false;

    const patched = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const isRequest = url.indexOf(target) === 0;
      const p = current.apply(this, [input, init]);
      if (!isRequest || !p || typeof p.then !== 'function') return p;

      return p.then((res) => {
        try {
          // Read a CLONE so the switcher still gets an unconsumed body.
          res.clone().json().then((body) => {
            if (!body || body.status !== 'rate-limited' || body.reason === 'limit:concurrent') return;
            let name = '';
            try { name = JSON.parse(String((init && init.body) || '{}')).name || ''; } catch (_) { }
            openLimitPopup(name, body.message);
          }).catch(() => { });
        } catch (_) { }
        return res;
      });
    };

    patched.__segalLimitPatched = true;
    // Carry family's flag forward so name-necklace-family.js does not re-wrap us
    // in a loop. Its stamp still applies - we call straight through to it.
    if (current.__segalFamilyPatched) patched.__segalFamilyPatched = true;

    window.fetch = patched;
    return true;
  };

  wrap();

  let ticks = 0;
  const iv = setInterval(() => {
    ticks += 1;
    wrap();
    if (ticks > 30) clearInterval(iv);
  }, 500);
  ['pointerdown', 'keydown'].forEach((evt) => {
    document.addEventListener(evt, () => { wrap(); }, true);
  });
})();
